<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import NewHook from '../NewHook.vue';
import ConfigurationPage from './ConfigurationPage.vue';

const { t } = useI18n();
const store = useStore();
const { replaceInstallationName } = useBranding();
// Bound via a variable (not a literal) so Vite doesn't try to statically
// resolve/bundle this as a module import — it's a public/ asset URL string.
const logoSrc = '/dashboard/images/integrations/meta_business_agent.png';

const integrationLoaded = ref(false);
const showAddHookModal = ref(false);
const showDeleteConfirmationPopup = ref(false);

const integrationGetter = useMapGetter('integrations/getIntegration');
const integration = computed(() =>
  integrationGetter.value('meta_business_agent')
);
const hook = computed(() => integration.value.hooks?.[0]);

const fetchIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(fetchIntegration);

const openAddHookModal = () => {
  showAddHookModal.value = true;
};
const hideAddHookModal = () => {
  showAddHookModal.value = false;
};
const openDeletePopup = () => {
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: hook.value.id,
      appId: 'meta_business_agent',
    });
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDeletePopup();
  }
};
</script>

<template>
  <div v-if="!integrationLoaded" class="flex items-center justify-center p-8">
    <Spinner size="" color-scheme="primary" />
  </div>
  <div v-else class="max-w-6xl p-4 mx-auto w-full">
    <div
      v-if="!hook"
      class="flex flex-col gap-4 p-6 rounded-md outline outline-1 outline-n-container bg-n-alpha-3"
    >
      <div class="flex items-center gap-4">
        <img
          :src="logoSrc"
          class="h-12 w-12 rounded-md border border-n-weak shadow-sm"
        />
        <div>
          <h3 class="mb-1 text-xl font-medium text-n-slate-12">
            {{ integration.name }}
          </h3>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ replaceInstallationName(integration.description) }}
          </p>
        </div>
      </div>
      <p class="text-n-slate-11 text-sm">
        {{ t('INTEGRATION_SETTINGS.META_BUSINESS_AGENT.CONNECT.DESCRIPTION') }}
      </p>
      <div>
        <NextButton
          :label="t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
          @click="openAddHookModal"
        />
      </div>
    </div>

    <ConfigurationPage
      v-else
      :hook-id="hook.id"
      :inbox-name="hook.inbox?.name"
      @disconnect="openDeletePopup"
    />

    <woot-modal v-model:show="showAddHookModal" :on-close="hideAddHookModal">
      <NewHook integration-id="meta_business_agent" @close="hideAddHookModal" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('INTEGRATION_APPS.DELETE.TITLE.INBOX')"
      :message="t('INTEGRATION_APPS.DELETE.MESSAGE.INBOX')"
      :confirm-text="t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
